// src/components/MessageDetail.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import DOMPurify from 'dompurify';
import { Trash2, Mail } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/spinner';

export type UserMessage = {
  id: string;
  senderString?: string | null;
  recipientString?: string | null;
  subject: string;
  body: string;
  createdAt: string;
  toJson?: string[];
  ccJson?: string[];
  replyJson?: string[];
  attachments?: { filename: string; data: string; mimeType?: string }[];
  userWhitelist?: string[];
};

// helper to extract the single-element type from the optional attachments array
type Attachment = NonNullable<UserMessage['attachments']>[0];

// Reusable attachment preview + download
export const AttachmentView = ({ attachment }: { attachment: { filename: string; data: string; mimeType?: string } }) => {
  const { filename, data, mimeType } = attachment;
  const ext = filename.split('.').pop()?.toLowerCase();

  if (ext === 'pdf') {
    return (
      <div className="h-full flex flex-col">
        <div className="text-sm font-medium text-gray-700 mb-2">{filename}</div>
        <object
          data={`data:${mimeType};base64,${data}`}
          type="application/pdf"
          width="100%"
          height="100%"
          className="border border-gray-200 rounded-lg flex-1"
        >
          <p className="text-sm text-gray-500">
            PDF preview not supported.{' '}
            <a href={`data:${mimeType};base64,${data}`} download className="underline text-blue-600">
              Download
            </a>
          </p>
        </object>
      </div>
    );
  }

  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext || '')) {
    return (
      <div className="h-full overflow-auto">
        <div className="text-sm font-medium text-gray-700 mb-2">{filename}</div>
        <img
          src={`data:${mimeType};base64,${data}`}
          alt={filename}
          className="w-full rounded-lg border border-gray-200"
        />
      </div>
    );
  }

  // Fallback: download link
  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-gray-700 mb-1">{filename}</div>
      <a href={`data:${mimeType};base64,${data}`} download className="text-sm underline text-blue-600">
        Download {filename}
      </a>
    </div>
  );
};

// Header for thread metadata
const TheadHeader = ({ thread }: any) => (
  <div className="mb-4">
    <div className="text-lg font-semibold truncate">{thread.subject}</div>
    <div className="text-sm text-gray-600 mt-1">
      {thread.senderString} | {new Date(thread.createdAt).toLocaleString()}
    </div>
  </div>
);

// Single message rendering: HTML body + attachments popup
const ThreadMessage = ({ message }: { message: UserMessage }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  // Initialize as null, with correct element type
  const [currentAttachment, setCurrentAttachment] = useState<Attachment | null>(null);

  const openPreview = (att: Attachment) => {
    setCurrentAttachment(att);
    setPreviewOpen(true);
  };

  return (
    <div className="space-y-4">
      <div
        className="prose max-w-none"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.body) }}
      />

      {message.attachments?.length ? (
        <div className="inline-flex flex-wrap space-x-3 mb-4">
          {message.attachments.map((att, idx) => (
            <button
              key={idx}
              className="attachment-item text-blue-600 underline"
              onClick={() => openPreview(att)}
            >
              {att.filename}
            </button>
          ))}
        </div>
      ) : null}

      {currentAttachment && (
        <div className="preview-content mb-4">
          {currentAttachment.mimeType?.startsWith('image/') ? (
            <img
              src={`data:${currentAttachment.mimeType};base64,${currentAttachment.data}`}
              alt={currentAttachment.filename}
              className="w-full rounded-lg border border-gray-200"
            />
          ) : (
            <a
              href={`data:${currentAttachment.mimeType};base64,${currentAttachment.data}`}
              download={currentAttachment.filename}
              className="underline text-blue-600"
            >
              Download {currentAttachment.filename}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// Main thread detail with messages list
export const ThreadDetail = ({ getThread, onDelete, onReply, thread }: any) => {
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const threadId = thread.id;
  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState<Attachment | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [threadDetails, setThreadDetails] = useState<any>(thread);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log("threadId", threadId);
    setLoading(true);
    setMessages([]); // Clear previous messages immediately
    
    getThread(threadId).then(({ data, error }: any) => {
      console.log("getThread response - data:", data);
      console.log("getThread response - error:", error);
      
      if (error) {
        console.error("GraphQL error:", error);
        setLoading(false);
        return;
      }
      
      if (!data) {
        console.error("No data returned from getThread");
        setLoading(false);
        return;
      }
      
      const messageData = data.getUserMessage || data.getUserMessageByToken;
      console.log("messageData:", messageData);
      
      if (!messageData) {
        console.error("No getUserMessage or getUserMessageByToken in response");
        setLoading(false);
        return;
      }
      
      setMessages(messageData.messages || []);
      setThreadDetails(messageData);
      setLoading(false);
    }).catch((err: any) => {
      console.error("Error in getThread:", err);
      setLoading(false);
    });
  }, [threadId]);

  const allAttachments = useMemo(() =>
    messages.flatMap(msg => msg.attachments || []).map(att => ({
      filename: att.filename,
      data: att.data,
      mimeType: att.mimeType
    })),
  [messages]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full bg-white rounded-lg p-6 space-y-6">
      {/* Header + delete button */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <TheadHeader thread={thread} />
        </div>
        <div className="flex-shrink-0 flex space-x-2">
          <Button
            variant="link"
            className="text-blue-600 inline-flex items-center space-x-1"
            onClick={() => onReply(threadDetails)}
          >
            <Mail size={16} /> <span>Reply</span>
          </Button>
          <Button
            variant="link"
            className="text-blue-600 inline-flex items-center space-x-1"
            onClick={() => setConfirmOpen(true)}
          >
            <Trash2 size={16} /> <span>Delete</span>
          </Button>
        </div>
      </div>

      {/* Thread meta */}
     

      {/* Inline list of attachments, each opens preview modal */}
      {allAttachments.length > 0 && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Attachments</h3>
            <div className="inline-flex flex-wrap gap-2">
            {allAttachments.map((att, idx) => (
                <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => {
                    setCurrentAttachment(att);
                    setPreviewOpen(true);
                }}
                >
                {att.filename}
                </Button>
            ))}
            </div>
        </div>
      )}

      {/* Single-attachment preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[80vw] h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0 flex flex-row items-center justify-between border-b pb-2 mb-2">
                <DialogTitle className="text-lg font-semibold truncate flex-1 pr-4">
                    {currentAttachment?.filename}
                </DialogTitle>
                <div className="flex items-center gap-4">
                    {currentAttachment && (
                        <a
                        href={`data:${currentAttachment.mimeType};base64,${currentAttachment.data}`}
                        download={currentAttachment.filename}
                        className="text-sm underline text-blue-600 whitespace-nowrap"
                        >
                        Download
                        </a>
                    )}
                </div>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
                {currentAttachment && (
                <AttachmentView
                    attachment={{
                    filename: currentAttachment.filename,
                    data: currentAttachment.data,
                    mimeType: currentAttachment.mimeType
                    }}
                />
                )}
            </div>
        </DialogContent>
      </Dialog>

      {/* Messages with inline attachments */}
      {messages.map((msg, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg bg-white shadow mb-6"
        >
          {/* Message header */}
          <div className="flex justify-between items-center px-4 pt-4 pb-2 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:gap-4">
                <span className="text-gray-800 font-bold">
                From: {msg.senderString ?? 'Unknown Sender'}
                </span>
                <span className="text-gray-800">
                To: {msg.recipientString}
                </span>
            </div>
            <span className="text-sm text-gray-600 whitespace-nowrap ml-2">
              {new Date(msg.createdAt).toLocaleString()}
            </span>
          </div>

          {/* Message body */}
          <div
            className="prose max-w-none px-4 py-4"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.body) }}
          />

          {/* Inline attachments */}
          {msg.attachments && msg.attachments.length > 0 && (
            <div className="inline-flex flex-wrap space-x-3 px-4 pb-4 border-t pt-3 mt-2">
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Attachments:</span>
              {msg.attachments.map((att, i) => (
                <button
                  key={i}
                  className="underline text-blue-600 text-sm"
                  onClick={() => {
                    setCurrentAttachment(att);
                    setPreviewOpen(true);
                  }}
                >
                  {att.filename}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Global attachments modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-[80vw] h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0 flex flex-row items-center justify-between border-b pb-2 mb-2">
                <DialogTitle>All Attachments</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 overflow-auto flex-1 p-2">
            {allAttachments.map((att, idx) => (
                <AttachmentView key={idx} attachment={att} />
            ))}
            </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
            <DialogHeader>
                <DialogTitle>Confirm Delete?</DialogTitle>
            </DialogHeader>
            <div className="flex justify-end space-x-2 pt-4">
                <Button
                    variant="destructive"
                    onClick={() => { onDelete(thread.id); setConfirmOpen(false); }}
                >
                    Yes, Delete
                </Button>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
